/**
 * Premium "Hero Orbit" intro — Allyver signature.
 *
 * Visual design:
 *  - Hero title at center; an orbital ring with three category dots
 *    (e.g. Restaurant, Shop, Service) rotates slowly behind the text.
 *  - Each dot pulses on its turn under the title.
 *  - Wordmark eyebrow + accent underline.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const heroOrbit: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Ein System. Sieben Welten.";
  const sub = inputs.subtext ?? "";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.62;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const eyeSize = typeSize("sm", ctx.format) * 0.7;
  const orbitSize = isVertical ? 1100 : 980;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const orbitId = `${clipPrefix}-orbit`;
  const ringId = `${clipPrefix}-ring`;
  const dotsWrapId = `${clipPrefix}-dotwrap`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const eyeId = `${clipPrefix}-eye`;
  const lineId = `${clipPrefix}-line`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  const labels = ["Restaurant", "Shop", "Service", "Hotel", "Taxi", "Friseur", "Coach"];
  const dotCount = isVertical ? 6 : 7;
  const dotIds: string[] = [];
  const dots = Array.from({ length: dotCount })
    .map((_, i) => {
      const angle = (i / dotCount) * 360;
      const id = `${clipPrefix}-d${i}`;
      dotIds.push(id);
      const label = labels[i % labels.length];
      const colorPick = i % 3 === 0 ? accent : i % 3 === 1 ? themeColors.fg : "#a855f7";
      return `<div id="${id}" data-angle="${angle}" style="position:absolute;left:50%;top:50%;width:0;height:0;transform-origin:0 0;transform:rotate(${angle}deg);">
        <div style="position:absolute;left:${orbitSize / 2 - 18}px;top:-18px;display:flex;align-items:center;gap:14px;transform:rotate(${-angle}deg);">
          <div style="width:18px;height:18px;border-radius:9999px;background:${colorPick};box-shadow:0 0 18px ${colorPick};"></div>
          <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize * 0.7}px;letter-spacing:4px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.78)};white-space:nowrap;">${escapeHtml(label)}</div>
        </div>
      </div>`;
    })
    .join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div id="${orbitId}" style="position:absolute;left:50%;top:50%;width:${orbitSize}px;height:${orbitSize}px;margin-left:-${orbitSize / 2}px;margin-top:-${orbitSize / 2}px;">
    <div id="${ringId}" style="position:absolute;inset:0;border-radius:9999px;border:1.5px dashed ${withAlpha(themeColors.fg, 0.25)};"></div>
    <div id="${dotsWrapId}" style="position:absolute;inset:0;">${dots}</div>
  </div>

  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 32 : 24}px;padding:96px;z-index:5;">
    <div id="${eyeId}" style="display:flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:8px;text-transform:uppercase;color:${accent};opacity:0;">
      <span style="width:24px;height:2px;background:${accent};box-shadow:0 0 10px ${accent};"></span>
      <span>Allyver Signature</span>
      <span style="width:24px;height:2px;background:${accent};box-shadow:0 0 10px ${accent};"></span>
    </div>

    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 880 : 1300}px;text-shadow:0 0 60px ${withAlpha(accent, 0.5)};opacity:0;">${escapeHtml(headline)}</div>

    <div id="${lineId}" style="height:2px;width:0;background:linear-gradient(90deg, transparent 0%, ${accent} 50%, transparent 100%);"></div>

    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.8)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;opacity:0;">${escapeHtml(sub)}</div>` : ""}
  </div>
</div>`;

  const init = [
    `gsap.set("#${orbitId}", { opacity: 0, scale: 0.85, rotation: -30, transformOrigin: "50% 50%" });`,
    `gsap.set("#${eyeId}", { opacity: 0, y: -16 });`,
    `gsap.set("#${headId}", { opacity: 0, y: 28 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
    `gsap.set("#${lineId}", { width: 0 });`,
    ...dotIds.map((id) => `gsap.set("#${id}", { opacity: 0 });`),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${orbitId}`, { opacity: 1, scale: 1, rotation: 0, duration: 1.0, ease: "raw:\"power3.out\"" }, 0.05),
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.4),
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power3.out\"" }, 0.55),
    t.to(`#${lineId}`, { width: isVertical ? 280 : 360, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.85),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.0));

  // Stagger dot fade-in.
  dotIds.forEach((id, i) => {
    tweens.push(t.to(`#${id}`, { opacity: 1, duration: 0.4, ease: "raw:\"power2.out\"" }, 0.5 + i * 0.07));
  });

  // Continuous slow orbit.
  const dwell = Math.max(0.6, clipDurationSec - 1.5);
  tweens.push(t.to(`#${orbitId}`, { rotation: 360, duration: dwell + 1.0, ease: "raw:\"none\"" }, 1.0));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
